import { useQuery } from '@tanstack/react-query';
import { Alert, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { getLead } from '../../../api/leads';
import { LeadDetail } from '../../../types/lead';

interface Step8Props {
  leadId: number | null;
}

const summaryRow = (label: string, value?: string | number | null) =>
  value !== undefined && value !== null && value !== '' ? (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  ) : null;

const RECOMMENDED_FIELDS: Array<{ label: string; check: (lead: LeadDetail) => boolean }> = [
  { label: 'Email', check: (l) => !!l.email },
  { label: 'Date of birth', check: (l) => !!l.dateOfBirth },
  { label: 'Nationality', check: (l) => !!l.nationalityCountry },
  { label: 'Preferred country', check: (l) => !!l.country },
  { label: 'Preferred qualification', check: (l) => !!l.preferredQualificationLevel },
  { label: "Father's or mother's name", check: (l) => !!(l.fatherName || l.motherName) },
  { label: 'Expected investment / funding source', check: (l) => !!(l.expectedInvestment || l.fundingSource) },
];

export const Step8Review = ({ leadId }: Step8Props) => {
  const leadQuery = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => getLead(leadId!),
    enabled: !!leadId,
  });

  if (!leadId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Complete Step 1 first.
      </Typography>
    );
  }
  if (leadQuery.isLoading || !leadQuery.data) return <CircularProgress size={24} />;

  const lead = leadQuery.data;
  const missing = RECOMMENDED_FIELDS.filter((f) => !f.check(lead));

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Review the details below before saving.
      </Typography>

      {missing.length > 0 && (
        <Alert severity="warning">
          Missing (optional but recommended): {missing.map((m) => m.label).join(', ')}
        </Alert>
      )}

      <Typography variant="subtitle2">Personal details</Typography>
      {summaryRow('Name', `${lead.firstName} ${lead.lastName}`)}
      {summaryRow('Gender', lead.gender)}
      {summaryRow('Date of birth', lead.dateOfBirth?.slice(0, 10))}
      {summaryRow('NIC / Passport', lead.nicOrPassport)}
      {summaryRow('Nationality', lead.nationalityCountry?.name)}
      {summaryRow('Mobile', lead.phone)}
      {summaryRow('Email', lead.email)}
      {summaryRow('Address', [lead.address, lead.city, lead.postalCode].filter(Boolean).join(', '))}
      {summaryRow('Branch', lead.branch?.name)}
      {summaryRow('Counselor', lead.assignedTo ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}` : undefined)}
      {summaryRow('Lead source', lead.leadSource.name)}

      <Divider />
      <Typography variant="subtitle2">Education</Typography>
      {summaryRow('School', lead.schoolName)}
      {summaryRow('O/L', [lead.olType, lead.olYear].filter(Boolean).join(' · '))}
      {summaryRow('A/L', [lead.alType, lead.alYear, lead.alStream].filter(Boolean).join(' · '))}
      {summaryRow('Overall GPA', lead.overallGpa)}

      <Divider />
      <Typography variant="subtitle2">Study preference</Typography>
      {summaryRow('Qualification', lead.preferredQualificationLevel?.name)}
      {summaryRow('Study area', lead.preferredStudyArea?.name)}
      {summaryRow('Country', lead.country?.name)}
      {summaryRow('University', lead.preferredUniversity?.name)}
      {summaryRow('Intake', lead.preferredIntake?.name)}
      {summaryRow('Budget', lead.budget ? `${lead.budgetCurrency?.code ?? ''} ${lead.budget}` : undefined)}

      <Divider />
      <Typography variant="subtitle2">Parents &amp; guardian</Typography>
      {summaryRow('Father', [lead.fatherName, lead.fatherPhone].filter(Boolean).join(' · '))}
      {summaryRow('Mother', [lead.motherName, lead.motherPhone].filter(Boolean).join(' · '))}
      {summaryRow('Guardian', [lead.guardianName, lead.guardianPhone].filter(Boolean).join(' · '))}

      <Divider />
      <Typography variant="subtitle2">Financial</Typography>
      {summaryRow('Expected investment', lead.expectedInvestment)}
      {summaryRow('Income range', lead.incomeRange)}
      {summaryRow('Scholarship required', lead.scholarshipRequired ? 'Yes' : 'No')}
      {summaryRow('Funding source', lead.fundingSource)}

      <Divider />
      <Typography variant="subtitle2">Marketing</Typography>
      {summaryRow('Ad ID / Name', lead.adIdName)}
      {summaryRow('Campaign', lead.campaign?.name)}
      {summaryRow('Referral type', lead.referralType?.name)}
      {summaryRow('Referrer', lead.referrerName)}
      {summaryRow('Recommended friends', lead.referredFriends.length ? `${lead.referredFriends.length} added` : undefined)}
    </Stack>
  );
};
